import {useEffect, useId, useState, type CSSProperties} from 'react';
import {createPortal} from 'react-dom';
import {TEAM_ROLES, getTeamRole, type TeamRoleId} from '../src/team';
import type {NativeAgentView, NativeTodoState} from '../src/native-hermes-types';
import type {ErrorDetails, HermesState, NativeSessionDetail} from './lib/types';
import {ErrorNotice, Loading, shortId} from './components';
import {parseNativeQuestion} from './native-question';
import {messageDisplayPrefix, userMessageDisplayText} from './lib/conversation-messages';
import './team-workspace.css';

type TeamUsage = NativeSessionDetail['state']['usage'];
export interface TeamWorkTask {
  id: string;
  title: string;
  group: 'in_progress' | 'needs_review' | 'next' | 'done';
  assignee: TeamRoleId | 'user' | {sessionId: string; name: string} | null;
  note?: string;
  reviewSessionId?: string;
}
export interface TeamMemberView {
  roleId: TeamRoleId;
  sessionId: string;
  running: boolean;
  executionState: 'working' | 'waiting_input' | 'waiting_agents' | 'resuming' | 'failed' | 'interrupted' | 'completed' | 'idle';
  status: string;
  activity: string | null;
  usage: TeamUsage | null;
}

export interface TeamDescendantView extends TeamMemberView {
  sessionId: string;
  name: string;
  parentSessionId: string | null;
}

export interface NativeTeamProjection {
  status: 'ready' | 'loading' | 'unavailable' | 'error';
  error: ErrorDetails | null;
  sessionId: string | null;
  observedAt: string | null;
  members: TeamMemberView[];
  descendants: TeamDescendantView[];
  tasks: TeamWorkTask[];
  mainTask: string | null;
}

export function teamMemberAtDesk(member: TeamMemberView): boolean {
  return member.executionState !== 'idle' && member.executionState !== 'completed';
}

export function teamChildInRoom(member: TeamDescendantView): boolean {
  return member.executionState === 'working' || member.executionState === 'resuming'
    || member.executionState === 'waiting_input' || member.executionState === 'waiting_agents' || member.executionState === 'failed';
}

function errorDetails(error: unknown): ErrorDetails {
  return error instanceof Error ? {name: error.name, message: error.message,
    ...(error.stack === undefined ? {} : {stack: error.stack})} : {name: 'TeamProjectionError', message: String(error)};
}

type RequestView = {sessionId: string; id: string; method: string; questions: Array<{id: string; question: string}>};

function snapshotRequests(detail: NativeSessionDetail): RequestView[] {
  const owned = new Set([detail.session.sessionId, ...detail.agents.map(agent => agent.sessionId)]);
  return detail.openRequests.flatMap(request => {
    const params = request.params;
    if (!params || typeof params !== 'object' || Array.isArray(params)) throw new Error('A native request has no parameter record.');
    const session = (params as Record<string, unknown>).session_id;
    if (typeof session !== 'string' || !session) throw new Error('A native request has no session owner.');
    const sessionId = session === detail.state.liveSessionId ? detail.session.sessionId : session;
    if (!owned.has(sessionId)) throw new Error(`Native request belongs to ${sessionId}, outside this chat.`);
    if (typeof request.id !== 'string' || typeof request.method !== 'string') throw new Error('A native request has no method or request ID.');
    if (request.method !== 'clarify') return [{sessionId, id: request.id, method: request.method, questions: []}];
    const parsed = parseNativeQuestion(request);
    const questions = parsed.questions.filter(question => !Object.hasOwn(parsed.locked, question.id));
    return questions.length ? [{sessionId, id: parsed.id, method: request.method, questions}] : [];
  });
}

export function nativeMainTask(detail: NativeSessionDetail | null, messagePrefix: string): string | null {
  const task = detail?.state.assignedTask;
  return task === undefined || task === null ? null : userMessageDisplayText(task, messagePrefix);
}

function snapshotTasks(detail: NativeSessionDetail, requests: RequestView[], actors: readonly TeamMemberView[], messagePrefix: string): TeamWorkTask[] {
  const tasks: TeamWorkTask[] = [];
  function appendTodos(sessionId: string, assignee: TeamWorkTask['assignee'], todoState: NativeTodoState | null): void {
    for (const todo of todoState?.todos ?? []) {
      if (todo.status === 'cancelled') continue;
      tasks.push({id: `${sessionId}:todo:${todo.id}`, title: userMessageDisplayText(todo.content, messagePrefix), assignee,
        group: todo.status === 'in_progress' ? 'in_progress' : todo.status === 'completed' ? 'done' : 'next'});
    }
  }
  appendTodos(detail.session.sessionId, 'head', detail.todoState);
  for (const agent of detail.agents) {
    const assignee = agent.roleId ?? {sessionId: agent.sessionId,
      name: agent.title === null ? shortId(agent.sessionId) : userMessageDisplayText(agent.title, messagePrefix, {folded: true})};
    appendTodos(agent.sessionId, assignee, agent.todoState);
    // An explicit native plan, including a cleared plan, owns its task rows.
    if (agent.todoState !== null || agent.assignedTask === null) continue;
    const actor = actors.find(member => member.sessionId === agent.sessionId);
    if (!actor) throw new Error(`The task owner ${agent.sessionId} is not in the workspace projection.`);
    const working = actor.executionState === 'working' || actor.executionState === 'resuming'
      || actor.executionState === 'waiting_input' || actor.executionState === 'waiting_agents';
    const completed = actor.executionState === 'completed';
    const stopped = actor.executionState === 'failed' || actor.executionState === 'interrupted';
    if (!working && !completed && !stopped) continue;
    tasks.push({id: `${agent.sessionId}:assignment`, title: userMessageDisplayText(agent.assignedTask, messagePrefix), assignee,
      group: working ? 'in_progress' : completed ? 'done' : 'next',
      ...(stopped ? {note: actor.executionState === 'failed' ? 'Execution error. Open the chat.' : 'Work stopped. Open the chat.'}
        : actor.executionState === 'waiting_input' || actor.executionState === 'waiting_agents' ? {note: actor.status} : {})});
  }
  for (const request of requests) {
    if (request.method !== 'clarify') {
      tasks.push({id: `request:${request.id}`, title: `Respond to ${request.method}`,
        group: 'needs_review', assignee: 'user', reviewSessionId: request.sessionId,
        note: 'This native request may need a response in the terminal.'});
      continue;
    }
    for (const question of request.questions) {
      tasks.push({id: `request:${request.id}:${question.id}`, title: question.question,
        group: 'needs_review', assignee: 'user', reviewSessionId: request.sessionId});
    }
  }
  return tasks;
}

const taskGroups = [
  {id: 'in_progress', name: 'In progress'}, {id: 'needs_review', name: 'Needs your review'},
  {id: 'next', name: 'Next'}, {id: 'done', name: 'Done'},
] as const;

export function TeamTaskList({mainTask, tasks, onSelect, onSelectSession, onReview}: {
  mainTask: string | null; tasks: readonly TeamWorkTask[];
  onSelect?: (roleId: TeamRoleId) => void;
  onSelectSession?: (sessionId: string) => void;
  onReview?: (sessionId: string) => void;
}) {
  return <section className="team-task-list" aria-label="Current tasks"><header className="team-main-task">
    <p className="eyebrow">Goal</p><h2>{mainTask === null ? `Give ${getTeamRole('head').name} a task.` : mainTask}</h2>
  </header><div className="team-task-groups">{taskGroups.map(group => {
    const rows = tasks.filter(task => task.group === group.id);
    if (rows.length === 0) return null;
    return <section className={`team-task-group team-task-group-${group.id}`} key={group.id} aria-label={group.name}>
      <h3>{group.name}</h3><ul>{rows.map(task => {
        const role = typeof task.assignee === 'string' && task.assignee !== 'user' ? getTeamRole(task.assignee) : null;
        const child = task.assignee !== null && typeof task.assignee === 'object' ? task.assignee : null;
        return <li key={task.id}><div className="team-task-content"><p>{task.title}</p>
          {task.note && <small>{task.note}</small>}
          {task.reviewSessionId && onReview && <button type="button" className="task-review-button" onClick={() => onReview(task.reviewSessionId!)}>Review</button>}</div>
          {task.assignee === 'user' && <span className="team-task-assignee">You</span>}
          {child && (onSelectSession ? <button type="button" className="team-task-assignee" onClick={() => onSelectSession(child.sessionId)}
            aria-label={`Open ${child.name} chat`}>{child.name}</button> : <span className="team-task-assignee">{child.name}</span>)}
          {role && (onSelect ? <button type="button" className="team-task-assignee" onClick={() => onSelect(role.id)}
            aria-label={`Open ${role.name} chat`} style={{'--agent-color': role.color} as CSSProperties}>{role.shortName}</button>
            : <span className="team-task-assignee" style={{'--agent-color': role.color} as CSSProperties}>{role.shortName}</span>)}
        </li>;
      })}</ul></section>;
  })}{tasks.length === 0 && <p className="team-task-empty">No tasks recorded yet.</p>}</div></section>;
}

export function NativeTeamTaskList({state, projection, onSelect, onSelectSession, onReview}: {
  state: HermesState; onSelect: (roleId: TeamRoleId) => void; onSelectSession: (sessionId: string) => void;
  projection?: NativeTeamProjection;
  onReview?: (sessionId: string) => void;
}) {
  const view = projection ?? nativeTeamProjection(state);
  if (view.status === 'error') return <section className="team-task-list"><ErrorNotice title="Cannot read the current tasks" error={view.error}/></section>;
  if (view.status === 'unavailable') return <section className="team-task-list">
    <p className="team-task-empty">Connect to read current tasks.</p></section>;
  if (view.status === 'loading') return <section className="team-task-list"><Loading/></section>;
  return <TeamTaskList mainTask={view.mainTask} tasks={view.tasks}
    onSelect={onSelect} onSelectSession={onSelectSession} {...(onReview ? {onReview} : {})}/>;
}

export function formatHourlyUsd(usage: TeamUsage | null): string {
  if (!usage) return 'Cost unavailable';
  if (usage.hourlyUsd === null || usage.pricingState === 'unpriced') return 'Unpriced';
  return formatUsd(usage.hourlyUsd);
}

function formatUsd(value: number): string {
  if (value > 0 && value < 0.0001) return '<$0.0001';
  return new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD', minimumFractionDigits: 2,
    maximumFractionDigits: 2}).format(value);
}

function metricTime(value: string): string {
  return new Intl.DateTimeFormat(undefined, {dateStyle: 'medium', timeStyle: 'medium'}).format(new Date(value));
}

export function AgentCostReadout({usage}: {usage: TeamUsage | null}) {
  const tooltipId = useId();
  const [visible, setVisible] = useState<{key: string; left: number; top: number} | null>(null);
  useEffect(() => {
    if (!visible) return;
    const close = () => setVisible(null);
    const keydown = (event: KeyboardEvent) => {if (event.key === 'Escape') close();};
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', keydown);
    return () => {
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('keydown', keydown);
    };
  }, [visible]);
  function show(key: string, element: HTMLElement): void {
    const anchor = element.getBoundingClientRect();
    setVisible({key, left: Math.max(12, Math.min(anchor.left, window.innerWidth - 292)),
      top: Math.max(12, Math.min(anchor.bottom + 9, window.innerHeight - 256))});
  }
  const task = usage?.task;
  const hourValue = usage?.pricingState === 'priced' && usage.hourlyUsd !== null ? formatUsd(usage.hourlyUsd) : '—';
  const taskValue = task?.pricingState === 'priced' && task.costUsd !== null ? formatUsd(task.costUsd) : '—';
  const hourDescription = !usage ? 'Average hourly cost is unavailable. Connect to read usage.'
    : `Average USD per hour. Recorded use from ${metricTime(usage.windowStart)} to ${metricTime(usage.windowEnd)}. `
      + (usage.pricingState === 'unpriced' ? 'Some usage has no reported price. ' : `${hourValue} per hour. `)
      + 'This agent only. Child agents have separate costs.';
  const taskDescription = !usage ? 'Assignment cost is unavailable. Connect to read usage.'
    : !task ? 'This agent has no recorded assignment cost.'
    : `Recorded cost for this agent’s latest assignment. Started ${metricTime(task.startedAt)}. `
      + (task.finishedAt === null ? 'The assignment is still open. ' : `Work ended ${metricTime(task.finishedAt)}. `)
      + `Usage received through ${metricTime(task.observedAt)}. `
      + (task.pricingState === 'unattributed' ? 'Some recorded use cannot be linked to this task. The total is unavailable. '
        : task.pricingState === 'unpriced' ? 'Some usage has no reported price. The total is unavailable. ' : `${taskValue} USD. `)
      + 'This agent only. Child agents have separate costs.';
  const metrics = [
    {key: 'hour', label: 'Average hourly cost', value: hourValue === '—' ? hourValue : `${hourValue}/h`, description: hourDescription},
    {key: 'task', label: 'Latest assignment cost', value: taskValue, description: taskDescription},
  ];
  return <div className="team-agent-costs">{metrics.map(metric => <span className="team-agent-metric-wrap" key={metric.key}
    onMouseEnter={event => show(metric.key, event.currentTarget)}
    onMouseLeave={event => {
      const focused = event.currentTarget.parentElement?.querySelector<HTMLElement>('.team-agent-metric:focus');
      if (focused?.dataset.metric) show(focused.dataset.metric, focused); else setVisible(null);
    }}>
    <span className="team-agent-metric" tabIndex={0} role="group" aria-label={`${metric.label}. ${metric.description}`}
      data-metric={metric.key}
      onFocus={event => show(metric.key, event.currentTarget)} onBlur={event => {if (!event.currentTarget.matches(':hover')) setVisible(null);}}
      aria-describedby={visible?.key === metric.key ? `${tooltipId}-${metric.key}` : undefined}>{metric.value}</span>
    {visible?.key === metric.key && createPortal(<span className="team-agent-metric-help" role="tooltip" id={`${tooltipId}-${metric.key}`}
      style={{left: visible.left, top: visible.top}}>{metric.description}</span>, document.body)}
  </span>)}</div>;
}

const executionLabels: Record<TeamMemberView['executionState'], string> = {
  working: 'Working', waiting_input: 'Needs your input', waiting_agents: 'Waiting for agents', resuming: 'Resuming', failed: 'Failed',
  interrupted: 'Interrupted', completed: 'Completed', idle: 'Idle',
};

function memberView(roleId: TeamRoleId, sessionId: string, live: NativeSessionDetail['state'] | NativeAgentView,
  todos: NativeTodoState | null, requests: RequestView[], messagePrefix: string): TeamMemberView {
  const ownedRequests = requests.filter(request => request.sessionId === sessionId);
  const executionState: TeamMemberView['executionState'] = ownedRequests.length ? 'waiting_input'
    : live.running ? 'working' : live.resuming ? 'resuming' : live.lifecycleStatus === 'error' ? 'failed'
    : live.lifecycleStatus === 'interrupted' ? 'interrupted' : live.pendingDelegations.length ? 'waiting_agents'
    : live.lifecycleStatus === 'complete' ? 'completed' : 'idle';
  const pendingQuestion = ownedRequests.flatMap(request => request.questions)[0]?.question;
  const active = executionState === 'working' || executionState === 'resuming';
  const activity = executionState === 'waiting_input' ? pendingQuestion ?? `Native request: ${ownedRequests[0]!.method}`
    : executionState === 'failed' ? live.error ?? live.endReason
    : executionState === 'interrupted' ? live.endReason
    : active ? live.currentTool ? `Using ${live.currentTool}` : live.activity?.trim()
      || userMessageDisplayText(todos?.todos.find(todo => todo.status === 'in_progress')?.content || live.assignedTask?.trim() || '', messagePrefix) || null : null;
  const status = executionState === 'waiting_agents' && !live.pendingDelegations.some(task => task.state === 'running' || task.state === 'finalizing')
    ? 'Waiting for results' : executionLabels[executionState];
  return {roleId, sessionId, running: live.running, executionState, status, activity, usage: live.usage};
}

export function nativeTeamProjection(state: HermesState): NativeTeamProjection {
  const empty: NativeTeamProjection = {status: 'unavailable', error: null, sessionId: null, observedAt: null,
    members: [], descendants: [], tasks: [], mainTask: null};
  if (state.nativeConnection.status === 'error') return {...empty, status: 'error', error: state.nativeConnection.error};
  if (state.nativeConnection.status === 'connecting') return {...empty, status: 'loading'};
  if (state.nativeConnection.status !== 'open') return empty;
  if (state.nativeDetail.status === 'error') return {...empty, status: 'error', error: state.nativeDetail.error};
  if (state.accountSettings.error) return {...empty, status: 'error', error: state.accountSettings.error};
  const messagePrefix = messageDisplayPrefix(state);
  if (messagePrefix === null) return {...empty, status: state.accountSettings.status === 'ready' ? 'unavailable' : 'loading'};
  const detail = state.nativeDetail.data;
  const target = state.nativeTarget?.sessionId;
  if (!detail || !target || detail.session.sessionId !== target) return {...empty, status: 'loading'};
  try {
    const requests = snapshotRequests(detail);
    const agents = new Map(detail.agents.map(agent => [agent.sessionId, agent]));
    if (agents.size !== detail.agents.length || agents.has(detail.session.sessionId)) throw new Error('The native snapshot contains repeated session IDs.');
    const primaryAgents = detail.agents.filter(agent => agent.roleId !== null);
    const roleIds = primaryAgents.map(agent => agent.roleId!);
    const repeatedRoles = [...new Set(roleIds.filter((roleId, index) => roleIds.indexOf(roleId) !== index))];
    if (repeatedRoles.length) throw new Error(`The native snapshot assigns these primary roles more than once: ${repeatedRoles.join(', ')}.`);
    const expectedRoles = TEAM_ROLES.filter(role => role.id !== 'head').map(role => role.id);
    const missingRoles = expectedRoles.filter(roleId => !roleIds.includes(roleId));
    const unexpectedRoles = roleIds.filter(roleId => !expectedRoles.includes(roleId));
    if (missingRoles.length || unexpectedRoles.length) throw new Error(`The native team roster does not match team.json. Missing roles: ${missingRoles.join(', ') || 'none'}. Unexpected roles: ${unexpectedRoles.join(', ') || 'none'}.`);
    for (const agent of primaryAgents) {
      if (agent.parentSessionId !== detail.session.sessionId) throw new Error(`The native primary agent ${agent.roleId} (${agent.sessionId}) has parent ${agent.parentSessionId ?? 'none'}; expected direct parent ${detail.session.sessionId}.`);
    }
    const members = [memberView('head', detail.session.sessionId, detail.state, detail.todoState, requests, messagePrefix),
      ...primaryAgents.map(agent => memberView(agent.roleId!, agent.sessionId, agent, agent.todoState, requests, messagePrefix))];
    const descendants = detail.agents.filter(agent => agent.roleId === null).map(agent => {
      let parent = agent.parentSessionId;
      let roleId: TeamRoleId | null = null;
      const seen = new Set<string>([agent.sessionId]);
      while (parent !== null) {
        if (seen.has(parent)) throw new Error(`Cycle in agent ancestry for ${agent.sessionId}.`);
        seen.add(parent);
        if (parent === detail.session.sessionId) {roleId = 'head'; break;}
        const ancestor = agents.get(parent);
        if (!ancestor) throw new Error(`Native agent ${agent.sessionId} has missing parent ${parent}.`);
        if (ancestor.roleId) {roleId = ancestor.roleId; break;}
        parent = ancestor.parentSessionId;
      }
      if (!roleId) throw new Error(`Native agent ${agent.sessionId} has no parent in this chat.`);
      return {...memberView(roleId, agent.sessionId, agent, agent.todoState, requests, messagePrefix),
        name: agent.title === null ? shortId(agent.sessionId) : userMessageDisplayText(agent.title, messagePrefix, {folded: true}),
        parentSessionId: agent.parentSessionId};
    });
    return {status: 'ready', error: null, sessionId: detail.session.sessionId, observedAt: detail.observedAt,
      members, descendants, tasks: snapshotTasks(detail, requests, [...members, ...descendants], messagePrefix), mainTask: nativeMainTask(detail, messagePrefix)};
  } catch (error) {return {...empty, status: 'error', error: errorDetails(error)};}
}

export function TeamUsageSummary({state, projection}: {state: HermesState; projection?: NativeTeamProjection}) {
  const view = projection ?? nativeTeamProjection(state);
  const available = view.status === 'ready';
  // The service returns each compression anchor once. Each usage view contains
  // only that agent's own events, so descendants enter this sum once too.
  const usage = available ? [...view.members, ...view.descendants].map(member => member.usage) : [];
  const priced = available && usage.every(item => item?.pricingState === 'priced' && item.hourlyUsd !== null);
  const total = priced ? usage.reduce((sum, item) => sum + item!.hourlyUsd!, 0) : null;
  const tokens = available ? usage.reduce((sum, item) => sum + item!.hourlyTokens, 0) : null;
  return <div className="team-usage-summary"><span>Selected chat burn rate <small>Recorded use in the last hour, including child agents</small></span>
    <strong>{total === null ? (available ? 'Unpriced' : 'Cost unavailable') : formatHourlyUsd({...usage[0]!, hourlyUsd: total})}<small>{total !== null ? 'USD / hour' : 'USD cost is not available'}</small></strong>
    <span className="team-usage-tokens" title="Recorded model token use during the previous hour.">{tokens === null ? 'Token count unavailable' : `${new Intl.NumberFormat().format(tokens)} tokens`}</span>
  </div>;
}
